// ---------------------------------------------------------------------------
// DataHub Service — Types and interface for the Teams Eval Data Hub API.
//
// API spec: POST /v1/datasets/search, POST /v1/datasets
// OpenAPI reference: teams-eval-data-hub-api.yaml
// ---------------------------------------------------------------------------

// ── Dataset types ─────────────────────────────────────────────────────────────

/** Lifecycle status matching the DataHub API enum (Draft → Published → Versioned). */
export type DatasetStatus = 'Draft' | 'Published' | 'Versioned';

// Re-exported from constants so consumers can import from either location.
import type {
  DatasetCreateDataType,
  DatasetRetentionOption,
  DatasetUsageRestriction,
} from '../constants/index.js';
export type {
  DatasetCreateDataType,
  DatasetRetentionOption,
  DatasetUsageRestriction,
};

import type { UsageContextOverrides } from './usageContext.interface.js';

/**
 * Sensitivity label sent to the API.
 * - Public access → always 'general' (auto-set, not shown in UI).
 * - Restricted access → user picks 'confidential' or 'highlyConfidential'.
 */
export type DatasetSensitivityLabel =
  | 'general'
  | 'confidential'
  | 'highlyConfidential';

/** Subset of sensitivity labels the user can explicitly choose (Restricted access only). */
export type DatasetRestrictedSensitivityLabel = Exclude<
  DatasetSensitivityLabel,
  'general'
>;

/**
 * Access-level filter used in the UI.
 * Note: not a native field in the base API DataSet schema — derived from
 * group roles or dataset metadata when available.
 * 'Public' = tenant-wide visibility; 'Restricted' = ACG-gated.
 */
export type DatasetAccessLevel = 'Public' | 'Restricted';

/** A dataset record, mapping the API DataSet schema to our app model. */
export interface Dataset {
  /** Maps to API field `dataSetId`. */
  id: string;
  name: string;
  description?: string;
  /** Monotonically increasing version number. */
  version: number;
  /** Latest published version reported by the dataset search API. */
  latestPublishedVersion?: number;
  status: DatasetStatus;
  createdAt?: string;
  updatedAt?: string;
  // ── Optional enrichment (not in base API DataSet schema) ──────────────────
  /** Populated from group-role data or dataset metadata when available. */
  accessLevel?: DatasetAccessLevel;
  /** Free-form data type label (e.g. "Meeting Data", "Chat Data"). */
  dataType?: string;
  /** Display name of the dataset creator from the API `creatorName` field. */
  creatorName?: string;
  /** Alias of the dataset creator/owner. */
  ownerAlias?: string;
  /** Approximate count of data entries in the dataset. */
  rowCount?: number;
  /** Semantic tags from the EntityTags API. */
  tags?: string[];
  // ── Creation metadata surfaced on the Overview tab ────────────────────────
  /** Sensitivity label of the dataset (API `sensitivityLabel`). */
  sensitivityLabel?: string;
  /**
   * Usage-restriction values (API `usageRestriction`, an array of enum names
   * such as `unrestricted` / `evalOnly`).
   */
  usageRestriction?: string[];
  /** Free-form sub-type label (API `subType`, e.g. "meetings-v2"). */
  subType?: string;
  /** Free-form source label (API `source`, e.g. "teams"). */
  source?: string;
  /** Retention-option enum name (API `retentionOption`, e.g. "days30"). */
  retentionOption?: string;
  /** Arbitrary source-property bag (API `sourceProperties`). */
  sourceProperties?: Record<string, unknown> | null;
}

// ── Dataset version types ────────────────────────────────────────────────────

/** A single version entry from the dataset version history API. */
export interface DatasetVersion {
  id: string;
  name: string;
  version: number;
  status: DatasetStatus;
  createdAt?: string;
  updatedAt?: string;
}

/** Parameters for GET /datasets/{dataSetId}/versions. */
export interface DatasetVersionListParams {
  dataSetId: string;
  pageSize?: number;
  continuationToken?: string;
  includeCount?: boolean;
}

/** Paginated result from `listDatasetVersions`. */
export interface DatasetVersionListResult {
  items: DatasetVersion[];
  totalCount: number | null;
  continuationToken: string | null;
}

// ── Dataset usage types ───────────────────────────────────────────────────────

/** CRUD category TEDH derives from the HTTP verb. */
export type DatasetUsageCategory = 'Create' | 'Read' | 'Update' | 'Delete';

/** Time-bucket granularity for the usage query. */
export type DatasetUsageGroupBy = 'Day' | 'Week' | 'Month';

/**
 * A single aggregated usage row — one per (time-bucket × category × action ×
 * application), never a raw per-event record.
 */
export interface DatasetUsageRecord {
  /** Start of the time bucket (UTC ISO-8601). */
  date: string;
  category: DatasetUsageCategory;
  /** Caller-supplied action; `"Unknown"` when no hint was sent. */
  action: string;
  /** Calling application (friendly name; raw app id if unmapped). */
  application: string;
  /** Number of interactions in the bucket. */
  count: number;
  /**
   * Distinct users in the bucket. Not additive across rows — a user active in
   * two buckets is counted in each.
   */
  uniqueUsers: number;
}

/** Result of `getDatasetUsage` — aggregated usage rows for a dataset. */
export interface DatasetUsageResult {
  records: DatasetUsageRecord[];
}

/** Parameters for `getDatasetUsage`. */
export interface GetDatasetUsageParams {
  dataSetId: string;
  /** Inclusive start (UTC ISO-8601). Defaults server-side to `to − 30 days`. */
  from?: string;
  /** Inclusive end (UTC ISO-8601). Defaults server-side to now. */
  to?: string;
  /** Time-bucket granularity. Defaults server-side to `Day`. */
  groupBy?: DatasetUsageGroupBy;
  /** Filter to a single CRUD category. */
  category?: DatasetUsageCategory;
  /** Filter to a single application (friendly name or raw app id). */
  application?: string;
}

// ── Attribute definition types ────────────────────────────────────────────────

/** An attribute definition from the DataHub API (schema: AttributeDefinition). */
export interface AttributeDefinition {
  id: string;
  attributeDefinitionId: string;
  /** Stable key used as the property name in data entry filters. */
  name: string;
  displayName?: string;
  description?: string;
  valueType?: string;
  /** When non-empty the value must come from this list → show as a dropdown. */
  allowedValues?: string[];
  isMultiValue: boolean;
  isRequired: boolean;
  isActive: boolean;
}

/** Parameters for POST /attributes/search. */
export interface SearchAttributeDefinitionsParams {
  isActive?: boolean;
  pageSize?: number;
  continuationToken?: string;
  includeCount?: boolean;
}

/** Paginated result returned by `searchAttributeDefinitions`. */
export interface AttributeDefinitionListResult {
  items: AttributeDefinition[];
  totalCount: number | null;
  continuationToken: string | null;
}

// ── Data entry types ──────────────────────────────────────────────────────────

/** A file attached to a data entry (maps the API GenericFile schema). */
export interface DataFile {
  id: string;
  properties?: Record<string, unknown>;
  fileName?: string;
  fileSizeInBytes?: number;
  fileType?: string;
  subType?: string;
}

/** A single data entry within a dataset (maps the API GenericData schema). */
export interface DataEntry {
  /** Internal record ID (API `id`). */
  id: string;
  /** Stable logical data ID shown to users (API `dataId`). */
  dataId: string;
  name?: string;
  dataType?: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
  version: number;
  /** Arbitrary key-value properties stored on the entry. */
  properties?: Record<string, unknown>;
  /** Files attached to this entry — populated by the detail endpoint. */
  dataFiles?: DataFile[];
  // ── Meeting-specific fields (present when dataType is `meetingData`) ────────
  /** Meeting subject/title. */
  meetingTitle?: string;
  /** BCP-47 language tag of the meeting (e.g. `en-us`). */
  language?: string;
  /** Meeting date (ISO `yyyy-mm-dd`). */
  meetingDate?: string;
  /** Entra (AAD) object ID of the meeting organizer. */
  organizerId?: string;
}

/** Parameters for GET /datasets/{dataSetId}/data. */
export interface DataEntryListParams {
  dataSetId: string;
  /** REQUIRED by the API — the dataset version to query. */
  dataSetVersion: number;
  pageSize?: number;
  continuationToken?: string;
  includeCount?: boolean;
}

/** Paginated result from `listDataEntries`. */
export interface DataEntryListResult {
  items: DataEntry[];
  totalCount: number | null;
  continuationToken: string | null;
}

/**
 * Result of hard-deleting all data belonging to a participant.
 * Backed by DELETE /datasets/{dataSetId}/participants/data.
 */
export interface DeleteParticipantDataResult {
  deletedCount: number;
  deletedDataIds: string[];
  emailOrAlias: string | null;
}

/** Parameters for GET /datasets/{dataSetId}/participants/data. */
export interface SearchDataEntriesByParticipantParams {
  dataSetId: string;
  /** Participant email or alias to match against meeting data entries. */
  emailOrAlias: string;
  pageSize?: number;
  continuationToken?: string;
  /** When true, the response includes the total item count across all pages. */
  includeCount?: boolean;
}

/** Paginated result of GET /datasets/{dataSetId}/participants/data. */
export interface ParticipantDataSearchResult {
  items: DataEntry[];
  totalCount: number | null;
  continuationToken: string | null;
}

// ── Data entry tag search types ──────────────────────────────────────────────

/** A single attribute filter for POST /datasets/{dataSetId}/data/search/by-tags. */
export interface DataEntryTagFilterInput {
  attributeDefinitionId: string;
  values: string[];
}

/** Parameters for POST /datasets/{dataSetId}/data/search/by-tags. */
export interface SearchDataEntriesByTagsParams {
  dataSetId: string;
  /** At least one filter is required by the API. */
  filters: DataEntryTagFilterInput[];
  /** 0 = AND, 1 = OR. */
  matchMode: 0 | 1;
  pageSize?: number;
  continuationToken?: string;
}

/** Paginated result from `searchDataEntriesByTags`. */
export interface DataEntryTagSearchResult {
  items: DataEntry[];
  continuationToken: string | null;
}

// ── Dataset tag types ─────────────────────────────────────────────────────────

/** A single attribute tag on a dataset, from GET /datasets/{id}/tags. */
export interface DatasetTag {
  attributeDefinitionId: string;
  attributeName: string;
  /** Current values — always at least one element when a tag is present. */
  values: string[];
}

/** Result of GET /datasets/{id}/tags. */
export interface DatasetTagsResult {
  entityId: string;
  tags: DatasetTag[];
}

/** Request body for PUT /datasets/{id}/tags — replaces all tags. */
export interface UpsertDatasetTagsRequest {
  /** Tags to set. Omitting a tag removes it. Each values array needs minItems 1. */
  tags: { attributeDefinitionId: string; values: string[] }[];
}

// ── Request / response shapes ─────────────────────────────────────────────────

/** Parameters for POST /v1/datasets/search. */
export interface SearchDatasetsParams {
  /**
   * Server-side full-text filter (tokenized full-word matching, NOT a
   * substring match — "te" will not match "test"). For substring search the
   * webapp filters client-side instead; see `useDatasets`.
   */
  query?: string;
  /** Filters by lifecycle status. */
  status?: DatasetStatus;
  /** Filters by access level (server-side). Maps to API integer enum. */
  accessCategory?: DatasetAccessLevel;
  /** Filters by data type (server-side). Maps to API integer enum. */
  dataType?: DatasetCreateDataType;
  /** Filters to datasets owned by this user (Entra OID). */
  ownerUserObjectId?: string;
  /** Maximum items per page (1–100, default 20). */
  pageSize?: number;
  /** Opaque pagination cursor from a previous response. */
  continuationToken?: string;
  /** When true, the response includes `totalCount`. */
  includeCount?: boolean;
}

/** Paginated result returned by `searchDatasets`. */
export interface DatasetSearchResult {
  items: Dataset[];
  /** Total matching records. Only set when `includeCount` was true. */
  totalCount: number | null;
  /** Pass as `continuationToken` to fetch the next page; null when exhausted. */
  continuationToken: string | null;
}

/** Payload for POST /v1/datasets (creates a Draft dataset). */
export interface CreateDatasetRequest {
  name: string;
  description?: string;
  accessCategory: DatasetAccessLevel;
  dataType: DatasetCreateDataType;
  sensitivityLabel: DatasetSensitivityLabel;
  usageRestriction?: DatasetUsageRestriction;
  subType?: string;
  source?: string;
  retentionOption?: DatasetRetentionOption;
  sourceProperties?: Record<string, unknown> | null;
}

/**
 * Payload for PUT /datasets/{dataSetId} (updates draft-version metadata).
 * Per the TEDH contract only these fields are editable after creation;
 * `dataType`, `accessCategory`, `sensitivityLabel`, `subType`, and
 * `retentionOption` are immutable and therefore omitted here.
 */
export interface UpdateDatasetMetaRequest {
  name: string;
  description?: string;
  source?: string;
  usageRestriction?: DatasetUsageRestriction;
  sourceProperties?: Record<string, unknown> | null;
}

/** Payload for POST /datasets/{dataSetId}/data (creates a new data entry). */
export interface CreateDataEntryRequest {
  name: string;
  dataType: DatasetCreateDataType;
  subType?: string;
  description?: string;
  properties?: Record<string, unknown>;
  // ── Chat-specific fields (sent only when dataType is 'Chat Data') ───────────
  channelId?: string;
  /** ISO-8601 timestamp of the first message. */
  startTime?: string;
  /** ISO-8601 timestamp of the last message. */
  endTime?: string;
  // ── Meeting-specific fields (sent only when dataType is 'Meeting Data') ─────
  /** Meeting subject/title. */
  meetingTitle?: string;
  /** BCP-47 language tag of the meeting (e.g. `en-us`). */
  language?: string;
  /** Meeting date (ISO `yyyy-mm-dd`). */
  meetingDate?: string;
  /** Entra (AAD) object ID of the meeting organizer. */
  organizerId?: string;
}

/** Payload for PUT /datasets/{dataSetId}/data (updates data entry metadata). */
export interface UpdateDataEntryRequest {
  dataId: string;
  name: string;
  dataType?: string;
  description?: string;
  properties?: Record<string, unknown>;
  // ── Meeting-specific fields (sent only when the entry is a meeting) ─────────
  /** Meeting subject/title. */
  meetingTitle?: string;
  /** BCP-47 language tag of the meeting (e.g. `en-us`). */
  language?: string;
  /** Meeting date (ISO `yyyy-mm-dd`). */
  meetingDate?: string;
  /** Entra (AAD) object ID of the meeting organizer. */
  organizerId?: string;
}

// ── Batch create-data types ───────────────────────────────────────────────────

export interface BatchItemError {
  dataId?: string;
  errorCode?: string;
  errorMessage?: string;
}

/** Result of POST /datasets/{dataSetId}/data/batch. */
export interface BatchCreateDataResult {
  succeeded: DataEntry[];
  failed: BatchItemError[];
}

// ── File upload types ────────────────────────────────────────────────────────

/** Result from generating an upload URL for a data entry file. */
export interface FileUploadUrlResult {
  fileId: string;
  uploadUrl: string;
}

/** File type string sent to the complete-upload endpoint. */
export type DataEntryFileType =
  | 'generic'
  | 'videoStream'
  | 'audioStream'
  | 'transcript';

/** Request body for completing a file upload (PUT /datasets/{id}/data/{dataId}/files). */
export interface CompleteFileUploadRequest {
  id: string;
  fileName: string;
  fileType: DataEntryFileType;
  fileSizeInBytes?: number;
  properties?: Record<string, unknown>;
}

// ── Annotation schema types ─────────────────────────────────────────────────

/** An annotation schema from the DataHub API. */
export interface AnnotationSchema {
  id: string;
  name: string;
  description?: string;
  /** Raw JSON string containing the schema definition. */
  schema: string;
  createdAt?: string;
}

/** Parameters for GET /annotation-schemas. */
export interface ListAnnotationSchemasParams {
  nameContains?: string;
  pageSize?: number;
  continuationToken?: string;
  includeCount?: boolean;
}

/** Paginated result from `listAnnotationSchemas`. */
export interface AnnotationSchemaListResult {
  items: AnnotationSchema[];
  totalCount: number | null;
  continuationToken: string | null;
}

/** Payload for POST /annotation-schemas. */
export interface CreateAnnotationSchemaRequest {
  name: string;
  description?: string;
  schema: string;
}

// ── Annotation set types ────────────────────────────────────────────────────

/** An annotation set within a dataset. */
export interface AnnotationSet {
  id: string;
  annotationSetId: string;
  dataSetId: string;
  schemaId: string;
  name: string;
  description?: string;
  status: DatasetStatus;
  version: number;
  createdAt?: string;
  updatedAt?: string;
  /** Entra object id of the set owner (the creator of the active version). */
  ownerUserObjectId?: string;
  /** When the active version was published; absent for an unpublished draft. */
  publishedAt?: string;
  /** Arbitrary key-value properties stamped on the set (e.g. stable keys). */
  properties?: Record<string, unknown>;
}

/** Parameters for GET /datasets/{dataSetId}/annotation-sets. */
export interface ListAnnotationSetsParams {
  dataSetId: string;
  pageSize?: number;
  continuationToken?: string;
  includeCount?: boolean;
}

/** Paginated result from `listAnnotationSets`. */
export interface AnnotationSetListResult {
  items: AnnotationSet[];
  totalCount: number | null;
  continuationToken: string | null;
}

/** Payload for POST /datasets/{dataSetId}/annotation-sets. */
export interface CreateAnnotationSetRequest {
  schemaId: string;
  name: string;
  description?: string;
  /** Stable key-value properties stamped on the set for rename-proof lookup. */
  properties?: Record<string, unknown>;
}

// ── Annotation data types ──────────────────────────────────────────────────

/** An annotation data entry within an annotation set (maps API GenericData). */
export interface AnnotationDataEntry {
  id: string;
  dataId: string;
  dataSetId?: string;
  name?: string;
  dataType?: string;
  rawDataId?: string;
  category?: string;
  contributor?: string;
  payload?: Record<string, unknown>;
  schemaId?: string;
  description?: string;
  properties?: Record<string, unknown>;
  version: number;
  createdAt?: string;
  updatedAt?: string;
  dataFiles?: DataFile[];
}

/** API integer enum for data types. */
export type AnnotationDataType = 0 | 1 | 2 | 3 | 4;

/** Payload for PUT /datasets/{dsId}/annotation-sets/{asId}/data/{dataId}. */
export interface UpdateAnnotationDataRequest {
  dataId: string;
  dataType: AnnotationDataType;
  name: string;
  subType?: string;
  description?: string;
  properties?: Record<string, unknown>;
}

/** Payload for POST /datasets/{dsId}/annotation-sets/{asId}/data. */
export interface CreateAnnotationDataRequest {
  dataType: AnnotationDataType;
  name: string;
  rawDataId: string;
  category: string;
  subType?: string;
  description?: string;
  properties?: Record<string, unknown>;
  contributor?: string;
  payload?: Record<string, unknown>;
  schemaId?: string;
}

/** Parameters for GET /datasets/{dsId}/annotation-sets/{asId}/data. */
export interface ListAnnotationDataParams {
  dataSetId: string;
  annotationSetId: string;
  category?: string;
  contributor?: string;
  pageSize?: number;
  continuationToken?: string;
  includeCount?: boolean;
}

/** Paginated result from `listAnnotationData`. */
export interface AnnotationDataListResult {
  items: AnnotationDataEntry[];
  totalCount: number | null;
  continuationToken: string | null;
}

/** Result from POST /datasets/{dsId}/annotation-sets/{asId}/data/batch. */
export interface BatchCreateAnnotationDataResult {
  total: number;
  succeededCount: number;
  failedCount: number;
  succeededItems: AnnotationDataEntry[];
  failedItems: BatchItemError[];
}

// ── Batch file upload types ──────────────────────────────────────────────────

/** A single data item's files to request upload URLs for. */
export interface BatchUploadUrlItem {
  dataId: string;
  files: { fileName: string }[];
}

/** A successfully resolved upload-URL set for one data item. */
export interface BatchUploadUrlSuccessItem {
  dataId: string;
  files: FileUploadUrlResult[];
}

/** A failed item from a batch file operation. */
export interface BatchFileFailedItem {
  dataId?: string;
  fileName?: string;
  errorMessage?: string;
  retryable: boolean;
}

/** Result from POST /datasets/{id}/files/batch (batch upload URLs). */
export interface BatchUploadUrlResult {
  succeededItems: BatchUploadUrlSuccessItem[];
  failedItems: BatchFileFailedItem[];
}

/** A single data item's files to complete after upload. */
export interface BatchCompleteFileItem {
  dataId: string;
  files: CompleteFileUploadRequest[];
}

// ── Per-entry batch file types ───────────────────────────────────────────────

/** Integer file type enum used by the per-entry batch complete endpoint. */
export const FILE_TYPE_INT = {
  generic: 0,
  videoStream: 1,
  audioStream: 2,
  transcript: 3,
} as const satisfies Record<DataEntryFileType, number>;

/** Result from POST /datasets/{dsId}/data/{dataId}/files/batch (per-entry batch upload URLs). */
export interface PerEntryBatchUploadUrlResult {
  total: number;
  succeededCount: number;
  failedCount: number;
  succeededItems: FileUploadUrlResult[];
  failedItems: BatchFileFailedItem[];
}

/** Request item for PUT /datasets/{dsId}/data/{dataId}/files/batch (per-entry batch complete). */
export interface PerEntryBatchCompleteFileRequest {
  id: string;
  fileType: DataEntryFileType;
  subType?: string;
  fileName: string;
  fileSizeInBytes?: number;
}

// ── Batch file download types ───────────────────────────────────────────────

/** A data item to request download URLs for. */
export interface BatchDownloadUrlItem {
  dataId: string;
}

/** A successfully resolved download-URL set for one data item. */
export interface BatchDownloadUrlSuccessItem {
  dataId: string;
  /** Map of fileId → SAS download URL. */
  files: Record<string, string>;
}

/** Result from POST /datasets/{id}/files/batch-download-urls. */
export interface BatchDownloadUrlResult {
  succeededItems: BatchDownloadUrlSuccessItem[];
  failedItems: BatchFileFailedItem[];
}

// ── Service interface ─────────────────────────────────────────────────────────

export interface IDataHubService {
  /**
   * Search datasets with optional name filter, status, and pagination.
   * Backed by POST /v1/datasets/search.
   */
  searchDatasets(params: SearchDatasetsParams): Promise<DatasetSearchResult>;

  /**
   * Create a new dataset in Draft state.
   * Backed by POST /v1/datasets.
   */
  createDataset(
    request: CreateDatasetRequest,
    usage?: UsageContextOverrides,
  ): Promise<Dataset>;

  /**
   * Get the latest version of a dataset by its logical ID.
   * Backed by GET /datasets/{id}/versions/latest.
   */
  getDataset(dataSetId: string): Promise<Dataset>;

  /**
   * Update the draft-version metadata of a dataset (name, description, source,
   * usage restriction, source properties).
   * Backed by PUT /datasets/{dataSetId}.
   */
  updateDatasetMeta(
    dataSetId: string,
    request: UpdateDatasetMetaRequest,
    usage?: UsageContextOverrides,
  ): Promise<Dataset>;

  /**
   * List data entries for a specific dataset version with pagination.
   * Backed by GET /datasets/{id}/data.
   */
  listDataEntries(params: DataEntryListParams): Promise<DataEntryListResult>;

  /**
   * Search attribute definitions with optional filters.
   * Backed by POST /attributes/search.
   */
  searchAttributeDefinitions(
    params: SearchAttributeDefinitionsParams,
  ): Promise<AttributeDefinitionListResult>;

  /**
   * Get all attribute tags for a dataset.
   * Backed by GET /datasets/{dataSetId}/tags.
   */
  getDatasetTags(dataSetId: string): Promise<DatasetTagsResult>;

  /**
   * Replace all attribute tags for a dataset (tags not listed are removed).
   * Backed by PUT /datasets/{dataSetId}/tags.
   */
  upsertDatasetTags(
    dataSetId: string,
    request: UpsertDatasetTagsRequest,
    usage?: UsageContextOverrides,
  ): Promise<DatasetTagsResult>;

  /**
   * Publish a draft dataset.
   * Backed by PATCH /datasets/{dataSetId}/publish.
   *
   * Emits a "Publish Version" TEDH usage event. Callers may pass `usage` to add
   * a correlation `sourceId` / `extras`; the action label is fixed.
   */
  publishDataset(
    dataSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<Dataset>;

  /**
   * Create a new draft version from a published dataset (demote to draft).
   * Backed by POST /datasets/{dataSetId}/versions/draft.
   */
  createDraftDataset(
    dataSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<Dataset>;

  /**
   * List version history for a dataset with pagination.
   * Backed by GET /datasets/{dataSetId}/versions.
   */
  listDatasetVersions(
    params: DatasetVersionListParams,
  ): Promise<DatasetVersionListResult>;

  /**
   * Get aggregated usage for a dataset (counts + unique users per bucket).
   * Owner-only — non-owner roles receive `403`.
   * Backed by GET /datasets/{dataSetId}/usage.
   */
  getDatasetUsage(params: GetDatasetUsageParams): Promise<DatasetUsageResult>;

  /**
   * Soft-delete a dataset (retains history, restorable).
   * Backed by DELETE /datasets/{dataSetId}.
   */
  deleteDataset(
    dataSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;

  /**
   * Permanently delete a dataset and all its data.
   * Backed by DELETE /datasets/{dataSetId}/hard.
   */
  hardDeleteDataset(
    dataSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;

  /**
   * Hard-delete the draft version of a dataset. If the draft is the only
   * version, the entire dataset is deleted. Returns 404 if no draft exists.
   * Backed by DELETE /datasets/{dataSetId}/versions/draft.
   */
  deleteDatasetDraft(
    dataSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;

  /**
   * Hard-delete all data containing the given participant (by email or alias),
   * across all versions (Draft, Published, Versioned).
   * Backed by DELETE /datasets/{dataSetId}/participants/data?emailOrAlias=.
   */
  hardDeleteByParticipant(
    dataSetId: string,
    emailOrAlias: string,
    usage?: UsageContextOverrides,
  ): Promise<DeleteParticipantDataResult>;

  /**
   * Search meeting data entries that contain the given participant (by email or
   * alias). Used for admin/compliance review before a participant purge.
   * Backed by GET /datasets/{dataSetId}/participants/data?emailOrAlias=.
   */
  searchDataEntriesByParticipant(
    params: SearchDataEntriesByParticipantParams,
  ): Promise<ParticipantDataSearchResult>;

  /**
   * Fetch a single data entry by its logical dataId, including attached files.
   * Backed by GET /datasets/{dataSetId}/data/{dataId}?dataSetVersion=.
   */
  getDataEntry(
    dataSetId: string,
    dataId: string,
    dataSetVersion: number,
  ): Promise<DataEntry>;

  /**
   * Get a short-lived download URL for a specific file on a data entry.
   * Backed by GET /datasets/{dataSetId}/data/{dataId}/files/{fileId}/url?dataSetVersion=.
   *
   * Opt-in usage: pass `usage` to record a "Download Dataset" TEDH event for a
   * deliberate single-file download; omit it for internal/non-user reads.
   */
  getDataEntryFileDownloadUrl(
    dataSetId: string,
    dataId: string,
    fileId: string,
    dataSetVersion: number,
    usage?: UsageContextOverrides,
  ): Promise<string>;

  /** Read a text file from a previously issued storage download URL. */
  readTextFileFromBlob(
    downloadUrl: string,
    signal?: AbortSignal,
    maxBytes?: number,
  ): Promise<string>;

  /**
   * Delete a specific file from a data entry.
   * Backed by DELETE /datasets/{dataSetId}/data/{dataId}/files/{fileId}.
   */
  deleteDataEntryFile(
    dataSetId: string,
    dataId: string,
    fileId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;

  /**
   * Create a new data entry in a dataset.
   * Backed by POST /datasets/{dataSetId}/data.
   *
   * Emits a "Create Data Entry" TEDH usage event. Callers may pass `usage` to
   * add a correlation `sourceId` / `extras`; the action label is fixed.
   */
  createDataEntry(
    dataSetId: string,
    request: CreateDataEntryRequest,
    usage?: UsageContextOverrides,
  ): Promise<DataEntry>;

  /**
   * Atomically POST many data entries to a dataset. The server returns a
   * partitioned `{ succeeded, failed }` result so callers can surface
   * per-item errors. Backed by POST /datasets/{dataSetId}/data/batch.
   *
   * Opt-in usage: pass `usage` to record a single "Upload Data (folder)" TEDH
   * event (folder upload). Callers that create entries for a different action
   * (e.g. synthetic-data generation) must omit `usage` so no event is recorded.
   */
  batchAddDataEntries(
    dataSetId: string,
    entries: CreateDataEntryRequest[],
    usage?: UsageContextOverrides,
  ): Promise<BatchCreateDataResult>;

  /**
   * Search data entries by attribute tags with AND/OR semantics.
   * Backed by POST /datasets/{dataSetId}/data/search/by-tags.
   */
  searchDataEntriesByTags(
    params: SearchDataEntriesByTagsParams,
  ): Promise<DataEntryTagSearchResult>;

  /**
   * Update data entry metadata.
   * Backed by PUT /datasets/{dataSetId}/data.
   *
   * Emits an "Update Data Entry" TEDH usage event. Callers may pass `usage` to
   * add a correlation `sourceId` / `extras`; the action label is fixed.
   */
  updateDataEntry(
    dataSetId: string,
    request: UpdateDataEntryRequest,
    usage?: UsageContextOverrides,
  ): Promise<DataEntry>;

  /**
   * Soft-delete a data entry.
   * Backed by DELETE /datasets/{dataSetId}/data/{dataId}.
   */
  deleteDataEntry(
    dataSetId: string,
    dataId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;

  /**
   * Permanently delete a data entry, its files, and all versions.
   * Backed by DELETE /datasets/{dataSetId}/data/{dataId}/hard.
   */
  hardDeleteDataEntry(
    dataSetId: string,
    dataId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;

  /**
   * Generate a short-lived SAS upload URL for a new file on a data entry.
   * Backed by POST /datasets/{dataSetId}/data/{dataId}/files.
   */
  generateFileUploadUrl(
    dataSetId: string,
    dataId: string,
    fileName: string,
    contentType?: string,
    usage?: UsageContextOverrides,
  ): Promise<FileUploadUrlResult>;

  /**
   * Upload file content directly to Azure Blob Storage via a SAS URL.
   */
  uploadFileToBlob(uploadUrl: string, file: File): Promise<void>;

  /**
   * Complete a file upload after the blob has been written.
   * Backed by PUT /datasets/{dataSetId}/data/{dataId}/files.
   */
  completeFileUpload(
    dataSetId: string,
    dataId: string,
    request: CompleteFileUploadRequest,
    usage?: UsageContextOverrides,
  ): Promise<DataEntry>;

  /**
   * Batch generate upload URLs for multiple files in one data entry.
   * Backed by POST /datasets/{dataSetId}/data/{dataId}/files/batch.
   */
  batchGenerateFileUploadUrls(
    dataSetId: string,
    dataId: string,
    fileNames: string[],
  ): Promise<PerEntryBatchUploadUrlResult>;

  /**
   * Batch complete multiple file uploads in one data entry.
   * Performs a single data-version update for all files.
   * Backed by PUT /datasets/{dataSetId}/data/{dataId}/files/batch.
   */
  batchCompleteFileUploads(
    dataSetId: string,
    dataId: string,
    requests: PerEntryBatchCompleteFileRequest[],
  ): Promise<DataEntry>;

  // ── Annotation schemas ──────────────────────────────────────────────────

  /**
   * List annotation schemas with optional name filter and pagination.
   * Backed by GET /annotation-schemas.
   */
  listAnnotationSchemas(
    params?: ListAnnotationSchemasParams,
  ): Promise<AnnotationSchemaListResult>;

  /**
   * Create a new annotation schema.
   * Backed by POST /annotation-schemas.
   */
  createAnnotationSchema(
    request: CreateAnnotationSchemaRequest,
  ): Promise<AnnotationSchema>;

  // ── Annotation sets ─────────────────────────────────────────────────────

  /**
   * List annotation sets for a dataset with pagination.
   * Backed by GET /datasets/{dataSetId}/annotation-sets.
   */
  listAnnotationSets(
    params: ListAnnotationSetsParams,
  ): Promise<AnnotationSetListResult>;

  /**
   * Create a new annotation set for a dataset.
   * Backed by POST /datasets/{dataSetId}/annotation-sets.
   */
  createAnnotationSet(
    dataSetId: string,
    request: CreateAnnotationSetRequest,
    usage?: UsageContextOverrides,
  ): Promise<AnnotationSet>;

  /**
   * Update an annotation set's name and/or description.
   * Backed by PUT /datasets/{dataSetId}/annotation-sets/{annotationSetId}.
   */
  updateAnnotationSet(
    dataSetId: string,
    annotationSetId: string,
    request: { name?: string; description?: string },
    usage?: UsageContextOverrides,
  ): Promise<AnnotationSet>;

  /**
   * Publish an annotation set (promote draft to published).
   * Backed by PATCH /datasets/{dataSetId}/annotation-sets/{annotationSetId}/publish.
   */
  publishAnnotationSet(
    dataSetId: string,
    annotationSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<AnnotationSet>;

  createAnnotationSetDraft(
    dataSetId: string,
    annotationSetId: string,
  ): Promise<AnnotationSet>;

  getAnnotationSetVersion(
    dataSetId: string,
    annotationSetId: string,
    version: number | 'draft',
  ): Promise<AnnotationSet>;

  /**
   * Soft-delete an annotation set.
   * Backed by DELETE /datasets/{dataSetId}/annotation-sets/{annotationSetId}.
   */
  deleteAnnotationSet(
    dataSetId: string,
    annotationSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;

  /**
   * Discard the draft version of an annotation set.
   * Backed by DELETE /datasets/{dataSetId}/annotation-sets/{annotationSetId}/versions/draft.
   */
  deleteAnnotationSetDraft(
    dataSetId: string,
    annotationSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;

  /**
   * Hard-delete an annotation set (permanent removal).
   * Backed by DELETE /datasets/{dataSetId}/annotation-sets/{annotationSetId}/hard.
   */
  hardDeleteAnnotationSet(
    dataSetId: string,
    annotationSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;

  // ── Annotation data ─────────────────────────────────────────────────────

  /**
   * List annotation data entries in an annotation set.
   * Backed by GET /datasets/{dsId}/annotation-sets/{asId}/data.
   */
  listAnnotationData(
    params: ListAnnotationDataParams,
  ): Promise<AnnotationDataListResult>;

  /** Read source-item annotations in a numeric version, requiring TEDH acknowledgement. */
  annotationReadSourceItems(
    dataSetId: string,
    annotationSetId: string,
    version: number,
    rawDataIds: string[],
  ): Promise<AnnotationDataEntry[]>;

  /**
   * Batch create annotation data entries.
   * Backed by POST /datasets/{dsId}/annotation-sets/{asId}/data/batch.
   *
   * @param usage Optional TEDH usage context. Opt-in: a folder upload calls
   *   this once per subfolder, so the orchestrator supplies `usage` on a single
   *   representative call to record one Upload Annotations event (D2).
   */
  batchCreateAnnotationData(
    dataSetId: string,
    annotationSetId: string,
    items: CreateAnnotationDataRequest[],
    usage?: UsageContextOverrides,
  ): Promise<BatchCreateAnnotationDataResult>;

  /**
   * Create a single annotation data entry.
   * Backed by POST /datasets/{dsId}/annotation-sets/{asId}/data.
   *
   * @param usage Optional TEDH usage context. Always-emit: a single annotation
   *   creation is a 1:1 user action (mirrors createDataEntry), so the header is
   *   sent whenever the global flag is on, even without overrides.
   */
  createAnnotationData(
    dataSetId: string,
    annotationSetId: string,
    request: CreateAnnotationDataRequest,
    usage?: UsageContextOverrides,
  ): Promise<AnnotationDataEntry>;

  /**
   * Update annotation data metadata.
   * Backed by PUT /datasets/{dsId}/annotation-sets/{asId}/data/{dataId}.
   * Only allowed when the annotation set is Draft.
   */
  updateAnnotationData(
    dataSetId: string,
    annotationSetId: string,
    dataId: string,
    request: UpdateAnnotationDataRequest,
    usage?: UsageContextOverrides,
  ): Promise<AnnotationDataEntry>;

  /**
   * Delete a single annotation data entry.
   * Backed by DELETE /datasets/{dsId}/annotation-sets/{asId}/data/{dataId}.
   */
  deleteAnnotationData(
    dataSetId: string,
    annotationSetId: string,
    dataId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;

  // ── Annotation file operations ────────────────────────────────────────

  /**
   * Generate an upload URL for a file on an annotation data entry.
   * Backed by POST /datasets/{dsId}/annotation-sets/{asId}/data/{dataId}/files.
   */
  annotationCreateUploadUrl(
    dataSetId: string,
    annotationSetId: string,
    dataId: string,
    fileName: string,
  ): Promise<FileUploadUrlResult>;

  /**
   * Complete a file upload on an annotation data entry.
   * Backed by PUT /datasets/{dsId}/annotation-sets/{asId}/data/{dataId}/files.
   */
  annotationCompleteUpload(
    dataSetId: string,
    annotationSetId: string,
    dataId: string,
    request: CompleteFileUploadRequest,
  ): Promise<AnnotationDataEntry>;

  /**
   * Get download URLs for all files on an annotation data entry.
   * Backed by GET /datasets/{dsId}/annotation-sets/{asId}/data/{dataId}/files/url.
   */
  annotationGetFileUrls(
    dataSetId: string,
    annotationSetId: string,
    dataId: string,
  ): Promise<Record<string, string>>;

  /**
   * Delete a file from an annotation data entry.
   * Backed by DELETE /datasets/{dsId}/annotation-sets/{asId}/data/{dataId}/files/{fileId}.
   */
  annotationDeleteFile(
    dataSetId: string,
    annotationSetId: string,
    dataId: string,
    fileId: string,
    usage?: UsageContextOverrides,
  ): Promise<void>;

  /**
   * Batch get download URLs for files across multiple annotation data entries.
   * Backed by POST /datasets/{dsId}/annotation-sets/{asId}/files/batch-download-urls.
   *
   * @param usage Optional TEDH usage context. Opt-in: the annotation download
   *   flow supplies `usage` on its single batch call to record one Download
   *   Annotations event (D2).
   */
  annotationBatchGetDownloadUrls(
    dataSetId: string,
    annotationSetId: string,
    version: string,
    items: BatchDownloadUrlItem[],
    usage?: UsageContextOverrides,
    requireVersionAcknowledgement?: boolean,
  ): Promise<BatchDownloadUrlResult>;

  // ── Batch file operations (dataset-level) ─────────────────────────────

  /**
   * Batch generate upload URLs for files across multiple data items.
   * Backed by POST /datasets/{dataSetId}/files/batch.
   */
  batchGetUploadUrls(
    dataSetId: string,
    items: BatchUploadUrlItem[],
  ): Promise<BatchUploadUrlResult>;

  /**
   * Batch complete file uploads across multiple data items.
   * Backed by PUT /datasets/{dataSetId}/files/batch.
   */
  batchCompleteFiles(
    dataSetId: string,
    items: BatchCompleteFileItem[],
  ): Promise<BatchCreateDataResult>;

  /**
   * Batch get SAS download URLs for all files across multiple data items.
   * Backed by POST /datasets/{dataSetId}/files/batch-download-urls.
   *
   * Opt-in usage: a full-dataset download calls this once per batch of entries;
   * pass `usage` on a single representative call so the whole download records
   * exactly one "Download Dataset" TEDH event, and omit it on the rest.
   */
  batchGetDownloadUrls(
    dataSetId: string,
    version: string,
    items: BatchDownloadUrlItem[],
    usage?: UsageContextOverrides,
  ): Promise<BatchDownloadUrlResult>;
}
